import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { StreamResource } from "@prequel/import-react";
import { useStream } from "@prequel/import-react";
import { Button, ButtonStyle, checkTabIsActive, PageHeading, Spinner, Tabs } from "@prequel/shared-components";

import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type StreamContext = {
  stream: StreamResource;
  refreshStream: () => void;
};

export function useStreamContext(): StreamContext {
  return useOutletContext<StreamContext>();
}

const StreamPage = () => {
  const streamID = useRequiredParam("streamID");
  const { addToast } = useToast();
  const { data: stream, error, isLoading, refetch: refreshStream } = useStream(streamID);

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load stream." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!stream) {
    return <Navigate to="/import/streams" replace />;
  }

  return <Outlet context={{ stream, refreshStream }} />;
};

export const StreamDetailsPage = () => {
  const navigate = useNavigate();
  const { stream, refreshStream } = useStreamContext();

  const tabs = [
    <NavLink key="Loads" to={`/import/streams/${stream.id}`} className={checkTabIsActive} end>
      Loads
    </NavLink>,
    <NavLink key="Details" to={`/import/streams/${stream.id}/details`} className={checkTabIsActive} end>
      Details
    </NavLink>,
  ];

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title={stream.name}
        onGoBack={() => navigate("/import/streams")}
        breadcrumbs={<Breadcrumbs dynamicLabel={stream.name} />}
      >
        <Button
          text="Edit"
          onClick={() => navigate(`/import/streams/${stream.id}/edit`)}
          type={ButtonStyle.SECONDARY}
        />
      </PageHeading>
      <div className="flex-1">
        <div className="relative">
          <div className="pt-0 pb-16">
            <div className="pb-6">
              <Tabs tabs={tabs} />
              <Outlet context={{ stream, refreshStream }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamPage;
